import React, { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "react-query";
import { useNavigate } from "react-router-dom";
import api from "../utils/axios";
import { useAuth } from "../contexts/AuthContext";
import { useSocket } from "../contexts/SocketContext";
import { showSuccessToast, showErrorToast } from "../utils/toastUtils";
import {
  TrendingUp,
  TrendingDown,
  ShoppingCart,
  DollarSign,
  Clock,
  Package,
  Menu as MenuIcon,
  AlertCircle,
  CheckCircle,
  XCircle,
  Eye,
  Calendar,
  BarChart3,
  Layers,
  Settings,
  Power,
  PowerOff,
  X,
} from "lucide-react";

const Dashboard = () => {
  const { vendor, updateVendorData } = useAuth();
  const { isConnected, notificationPermission, requestNotificationPermission } =
    useSocket();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [timeRange, setTimeRange] = useState("today");
  const [showNotificationPrompt, setShowNotificationPrompt] = useState(false);
  const [showStatusModal, setShowStatusModal] = useState(false);

  const { data: stats, isLoading: statsLoading } = useQuery(
    ["vendor-stats", timeRange],
    async () => {
      const response = await api.get(`/vendor/stats?timeRange=${timeRange}`);
      return response.data;
    },
    {
      refetchInterval: 30000, // Refresh every 30 seconds
    }
  );

  const {
    data: recentOrders,
    isLoading: ordersLoading,
    error: ordersError,
  } = useQuery(
    ["recent-orders"],
    async () => {
      try {
        const response = await api.get(
          "/vendor/orders?limit=10&status=pending,pending_approval,confirmed,preparing"
        );
        return response.data;
      } catch (error) {
        console.error(
          "Error fetching orders:",
          error.response?.data || error.message
        );
        throw error;
      }
    },
    {
      refetchInterval: 15000, // Refresh every 15 seconds for orders
      retry: 3, // Retry failed requests
      retryDelay: 1000, // Wait 1 second between retries
      onError: (error) => {
        console.error("Orders query error:", error);
      },
    }
  );

  const { data: menuStats } = useQuery(["menu-stats"], async () => {
    const response = await api.get("/vendor/menus");
    const menus = response.data;
    return {
      totalMenus: menus.length,
      activeMenus: menus.filter((m) => m.is_active).length,
      totalItems: menus.reduce(
        (sum, menu) => sum + (menu.menuItems?.length || 0),
        0
      ),
    };
  });

  // Vendor status update mutation
  const updateVendorStatusMutation = useMutation(
    async (status) => {
      const response = await api.patch("/api/vendors/status", { status });
      return response.data;
    },
    {
      onSuccess: async (data) => {
        // Refresh vendor data to get updated status
        try {
          const vendorResponse = await api.get("/auth/vendor/me");
          updateVendorData(vendorResponse.data);
        } catch (error) {
          console.error("Failed to refresh vendor data:", error);
        }

        queryClient.invalidateQueries(["vendor"]);
        showSuccessToast(
          `Restaurant status updated to ${data.status}`,
          "status-update"
        );
        setShowStatusModal(false);
      },
      onError: (error) => {
        showErrorToast(
          error.response?.data?.message || "Failed to update status",
          "status-update"
        );
      },
    }
  );

  // Handle notification permission
  useEffect(() => {
    if (notificationPermission === "default") {
      setShowNotificationPrompt(true);
    }
  }, [notificationPermission]);

  const handleEnableNotifications = async () => {
    const permission = await requestNotificationPermission();
    if (permission === "granted") {
      setShowNotificationPrompt(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "pending":
        return <Clock className="h-4 w-4 text-yellow-500" />;
      case "pending_approval":
        return <AlertCircle className="h-4 w-4 text-orange-500" />;
      case "confirmed":
        return <CheckCircle className="h-4 w-4 text-blue-500" />;
      case "preparing":
        return <AlertCircle className="h-4 w-4 text-blue-500" />;
      case "ready":
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case "completed":
        return <CheckCircle className="h-4 w-4 text-green-600" />;
      case "cancelled":
        return <XCircle className="h-4 w-4 text-red-500" />;
      default:
        return <Clock className="h-4 w-4 text-gray-500" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "pending":
        return "bg-yellow-100 text-yellow-800 border-yellow-200";
      case "pending_approval":
        return "bg-orange-100 text-orange-800 border-orange-200";
      case "confirmed":
        return "bg-blue-100 text-blue-800 border-blue-200";
      case "preparing":
        return "bg-blue-100 text-blue-800 border-blue-200";
      case "ready":
        return "bg-green-100 text-green-800 border-green-200";
      case "completed":
        return "bg-green-100 text-green-800 border-green-200";
      case "cancelled":
        return "bg-red-100 text-red-800 border-red-200";
      default:
        return "bg-gray-100 text-gray-800 border-gray-200";
    }
  };

  const statCards = [
    {
      title: "Total Orders",
      value: stats?.totalOrders || 0,
      change: stats?.orderChange || "+0%",
      changeType: stats?.orderChange?.startsWith("+") ? "positive" : "negative",
      icon: ShoppingCart,
      color: "bg-blue-500",
      description: "Orders this period",
    },
    {
      title: "Revenue",
      value: `£${stats?.totalRevenue || "0.00"}`,
      change: stats?.revenueChange || "+0%",
      changeType: stats?.revenueChange?.startsWith("+")
        ? "positive"
        : "negative",
      icon: DollarSign,
      color: "bg-green-500",
      description: "Revenue this period",
    },
    {
      title: "Menu Items",
      value: menuStats?.totalItems || 0,
      change: "+0",
      changeType: "positive",
      icon: Package,
      color: "bg-purple-500",
      description: "Active menu items",
    },
    {
      title: "Active Menus",
      value: menuStats?.activeMenus || 0,
      change: "+0",
      changeType: "positive",
      icon: MenuIcon,
      color: "bg-orange-500",
      description: "Published menus",
    },
  ];

  if (statsLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  return (
    <div className=" ">
      {/* Notification Permission Prompt */}
      {showNotificationPrompt && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                <span className="text-blue-600 text-lg">🔔</span>
              </div>
              <div>
                <h3 className="font-medium text-blue-900">
                  Enable Notifications
                </h3>
                <p className="text-sm text-blue-700">
                  Get instant alerts for new orders and status updates with
                  sound
                </p>
              </div>
            </div>
            <button
              onClick={handleEnableNotifications}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium"
            >
              Enable
            </button>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between space-y-4 md:space-y-0">
        <div>
          <h1 className="text-xl md:text-2xl font-bold text-secondary-900">
            Dashboard
          </h1>
          <p className="text-sm md:text-base text-secondary-600">
            Welcome back, {vendor?.name}! Here's what's happening today.
          </p>
        </div>

        {/* Time Range Selector */}
        <div className="flex items-center space-x-2">
          <label className="text-sm font-medium text-secondary-700">
            Time Range:
          </label>
          <select
            value={timeRange}
            onChange={(e) => setTimeRange(e.target.value)}
            className="input-field text-sm md:text-base px-3 py-2 rounded-lg border-secondary-200 focus:border-primary-500 focus:ring-2 focus:ring-primary-500"
          >
            <option value="today">Today</option>
            <option value="week">This Week</option>
            <option value="month">This Month</option>
            <option value="year">This Year</option>
          </select>
        </div>
      </div>

      {/* Stats Grid - Tablet Optimized */}
      <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4 lg:gap-6">
        {statCards.map((stat, index) => (
          <div
            key={index}
            className="card hover:shadow-md transition-all duration-200 animate-fade-in"
            style={{ animationDelay: `${index * 100}ms` }}
          >
            <div className="flex flex-col md:flex-row md:items-center md:justify-between space-y-3 md:space-y-0">
              <div className="flex-1">
                <p className="text-xs md:text-sm font-medium text-secondary-600">
                  {stat.title}
                </p>
                <p className="text-xl md:text-2xl font-bold text-secondary-900">
                  {stat.value}
                </p>
                <p className="text-xs text-secondary-500 mt-1">
                  {stat.description}
                </p>
                <div
                  className={`flex items-center mt-2 text-xs md:text-sm ${
                    stat.changeType === "positive"
                      ? "text-green-600"
                      : "text-red-600"
                  }`}
                >
                  {stat.changeType === "positive" ? (
                    <TrendingUp className="h-3 w-3 md:h-4 md:w-4 mr-1" />
                  ) : (
                    <TrendingDown className="h-3 w-3 md:h-4 md:w-4 mr-1" />
                  )}
                  {stat.change}
                </div>
              </div>
              <div
                className={`${stat.color} p-2 md:p-3 rounded-lg self-start md:self-auto`}
              >
                <stat.icon className="h-5 w-5 md:h-6 md:w-6 text-white" />
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Recent Orders - Tablet Optimized */}
      <div className="card">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-4 md:mb-6 space-y-3 md:space-y-0">
          <div>
            <h2 className="text-base md:text-lg font-semibold text-secondary-900">
              Recent Orders
            </h2>
            <p className="text-xs md:text-sm text-secondary-600">
              Latest orders requiring attention
            </p>
          </div>
          <button
            onClick={() => navigate("/orders")}
            className="text-primary-600 hover:text-primary-700 text-xs md:text-sm font-medium flex items-center self-start md:self-auto"
          >
            <Eye className="h-3 w-3 md:h-4 md:w-4 mr-1" />
            View All Orders
          </button>
        </div>

        {ordersLoading ? (
          <div className="flex items-center justify-center h-24 md:h-32">
            <div className="animate-spin rounded-full h-6 w-6 md:h-8 md:w-8 border-b-2 border-primary-500"></div>
            <span className="ml-2 text-secondary-600 text-sm md:text-base">
              Loading orders...
            </span>
          </div>
        ) : ordersError ? (
          <div className="text-center py-6 md:py-8">
            <AlertCircle className="h-8 w-8 md:h-12 md:w-12 text-red-400 mx-auto mb-3 md:mb-4" />
            <p className="text-red-600 text-sm md:text-base">
              Failed to load orders
            </p>
            <p className="text-xs md:text-sm text-red-500 mt-1">
              {ordersError.response?.data?.message || ordersError.message}
            </p>
            <button
              onClick={() => window.location.reload()}
              className="mt-3 md:mt-4 px-3 py-2 md:px-4 md:py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 text-sm"
            >
              Retry
            </button>
          </div>
        ) : !recentOrders || recentOrders.length === 0 ? (
          <div className="text-center py-6 md:py-8">
            <Package className="h-8 w-8 md:h-12 md:w-12 text-secondary-400 mx-auto mb-3 md:mb-4" />
            <p className="text-secondary-600 text-sm md:text-base">
              No pending orders
            </p>
            <p className="text-xs md:text-sm text-secondary-500">
              All caught up! New orders will appear here
            </p>
          </div>
        ) : (
          <div className="space-y-3 md:space-y-4">
            {recentOrders.map((order) => (
              <div
                key={order.id}
                className="flex flex-col md:flex-row md:items-center md:justify-between p-3 md:p-4 bg-secondary-50 rounded-lg border hover:shadow-sm transition-shadow space-y-3 md:space-y-0"
              >
                <div className="flex items-center space-x-3 md:space-x-4">
                  <div className="h-8 w-8 md:h-10 md:w-10 bg-primary-100 rounded-full flex items-center justify-center">
                    {getStatusIcon(order.status)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-secondary-900 text-sm md:text-base">
                      Order #{order.order_group_id || order.id}
                    </p>
                    <p className="text-xs md:text-sm text-secondary-600">
                      {order.orderItems?.length || 0} items • £
                      {order.subtotal || 0}
                    </p>
                    {order.orderItems && order.orderItems.length > 0 && (
                      <p className="text-xs text-secondary-500 max-w-full md:max-w-xs truncate">
                        {order.orderItems
                          .slice(0, 2)
                          .map((item) => {
                            return item.menuItem?.name || "Unknown Item";
                          })
                          .join(", ")}
                        {order.orderItems.length > 2 &&
                          ` +${order.orderItems.length - 2} more`}
                      </p>
                    )}
                    <p className="text-xs text-secondary-500">
                      <Calendar className="h-3 w-3 inline mr-1" />
                      {new Date(order.created_at).toLocaleDateString()} at{" "}
                      {new Date(order.created_at).toLocaleTimeString()}
                    </p>
                  </div>
                </div>
                <div className="text-left md:text-right">
                  <span
                    className={`inline-flex items-center px-2 py-1 md:px-3 md:py-1 rounded-full text-xs font-medium border ${getStatusColor(
                      order.status
                    )}`}
                  >
                    {(() => {
                      const statusLabels = {
                        pending: "Pending",
                        pending_approval: "Pending Approval",
                        confirmed: "Confirmed",
                        preparing: "Preparing",
                        ready: "Ready",
                        delayed: "Delayed",
                        at_pickup_point: "At Pickup Point",
                        out_for_delivery: "Out for Delivery",
                        delivered: "Delivered",
                        completed: "Completed",
                        cancelled: "Cancelled",
                      };
                      return statusLabels[order.status] || 
                        order.status.charAt(0).toUpperCase() + order.status.slice(1);
                    })()}
                  </span>
                  <div className="mt-2 md:mt-1 space-y-1">
                    <p className="text-xs text-secondary-500">
                      {order.order_type === "delivery"
                        ? "🚚 Delivery"
                        : order.order_type === "takeaway"
                        ? "📦 Takeaway"
                        : "🍽️ Dine-in"}
                    </p>
                    {order.payment_method && (
                      <p className="text-xs text-secondary-500">
                        {order.payment_method === "cash_at_counter"
                          ? "💵 Cash at Counter"
                          : "💳 Card Payment"}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Quick Actions - Tablet Optimized */}
      <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
        <div
          onClick={() => navigate("/menu-items")}
          className="card text-center hover:shadow-md transition-all duration-200 cursor-pointer group hover-lift"
        >
          <div className="h-10 w-10 md:h-12 md:w-12 bg-primary-100 rounded-lg flex items-center justify-center mx-auto mb-3 md:mb-4 group-hover:bg-primary-200 transition-colors">
            <Package className="h-5 w-5 md:h-6 md:w-6 text-primary-600" />
          </div>
          <h3 className="font-medium text-secondary-900 mb-1 md:mb-2 text-sm md:text-base">
            Add Menu Item
          </h3>
          <p className="text-xs md:text-sm text-secondary-600">
            Create new dishes for your menu
          </p>
        </div>

        <div
          onClick={() => navigate("/menus")}
          className="card text-center hover:shadow-md transition-all duration-200 cursor-pointer group hover-lift"
        >
          <div className="h-10 w-10 md:h-12 md:w-12 bg-green-100 rounded-lg flex items-center justify-center mx-auto mb-3 md:mb-4 group-hover:bg-green-200 transition-colors">
            <MenuIcon className="h-5 w-5 md:h-6 md:w-6 text-green-600" />
          </div>
          <h3 className="font-medium text-secondary-900 mb-1 md:mb-2 text-sm md:text-base">
            Manage Menus
          </h3>
          <p className="text-xs md:text-sm text-secondary-600">
            Organize your menu categories
          </p>
        </div>

        <div
          onClick={() => navigate("/addons")}
          className="card text-center hover:shadow-md transition-all duration-200 cursor-pointer group hover-lift"
        >
          <div className="h-10 w-10 md:h-12 md:w-12 bg-purple-100 rounded-lg flex items-center justify-center mx-auto mb-3 md:mb-4 group-hover:bg-purple-200 transition-colors">
            <Layers className="h-5 w-5 md:h-6 md:w-6 text-purple-600" />
          </div>
          <h3 className="font-medium text-secondary-900 mb-1 md:mb-2 text-sm md:text-base">
            Manage Add-ons
          </h3>
          <p className="text-xs md:text-sm text-secondary-600">
            Customize menu item options
          </p>
        </div>

        <div className="card text-center hover:shadow-md transition-all duration-200 cursor-pointer group hover-lift">
          <div className="h-10 w-10 md:h-12 md:w-12 bg-blue-100 rounded-lg flex items-center justify-center mx-auto mb-3 md:mb-4 group-hover:bg-blue-200 transition-colors">
            <BarChart3 className="h-5 w-5 md:h-6 md:w-6 text-blue-600" />
          </div>
          <h3 className="font-medium text-secondary-900 mb-1 md:mb-2 text-sm md:text-base">
            View Analytics
          </h3>
          <p className="text-xs md:text-sm text-secondary-600">
            Check your performance metrics
          </p>
        </div>
      </div>

      {/* Restaurant Status - Tablet Optimized */}
      <div className="card">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between space-y-3 md:space-y-0">
          <div>
            <h3 className="text-base md:text-lg font-semibold text-secondary-900">
              Restaurant Status
            </h3>
            <p className="text-xs md:text-sm text-secondary-600">
              Current operating status
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <span
              className={`inline-flex items-center px-2 py-1 md:px-3 md:py-1 rounded-full text-xs font-medium ${
                vendor?.status === "active"
                  ? "bg-green-100 text-green-800"
                  : "bg-red-100 text-red-800"
              }`}
            >
              {vendor?.status === "active" ? "🟢 Active" : "🔴 Closed"}
            </span>
            <span
              className={`inline-flex items-center px-2 py-1 md:px-3 md:py-1 rounded-full text-xs font-medium ${
                isConnected
                  ? "bg-green-100 text-green-800"
                  : "bg-yellow-100 text-yellow-800"
              }`}
            >
              {isConnected ? "🔌 Connected" : "⚠️ Disconnected"}
            </span>
            <button
              onClick={() => setShowStatusModal(true)}
              className="inline-flex items-center px-2 py-1 md:px-3 md:py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 hover:bg-blue-200 transition-colors"
            >
              <Settings className="h-3 w-3 mr-1" />
              Update Status
            </button>
          </div>
        </div>
      </div>

      {/* Status Update Modal */}
      {showStatusModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-secondary-900">
                Update Restaurant Status
              </h3>
              <button
                onClick={() => setShowStatusModal(false)}
                className="text-secondary-400 hover:text-secondary-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="space-y-4">
              <p className="text-sm text-secondary-600">
                Choose whether your restaurant is open for orders or temporarily
                closed.
              </p>

              <div className="space-y-3">
                <button
                  onClick={() => updateVendorStatusMutation.mutate("active")}
                  disabled={
                    updateVendorStatusMutation.isLoading ||
                    vendor?.status === "active"
                  }
                  className={`w-full p-4 rounded-lg border-2 transition-all ${
                    vendor?.status === "active"
                      ? "border-green-300 bg-green-50 text-green-700"
                      : "border-gray-200 hover:border-green-300 hover:bg-green-50"
                  } ${
                    updateVendorStatusMutation.isLoading
                      ? "opacity-50 cursor-not-allowed"
                      : "cursor-pointer"
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                        <Power className="h-4 w-4 text-green-600" />
                      </div>
                      <div className="text-left">
                        <p className="font-medium">Active</p>
                        <p className="text-sm opacity-75">
                          Restaurant is active and accepting orders
                        </p>
                      </div>
                    </div>
                    {vendor?.status === "active" && (
                      <CheckCircle className="h-5 w-5 text-green-600" />
                    )}
                  </div>
                </button>

                <button
                  onClick={() => updateVendorStatusMutation.mutate("closed")}
                  disabled={
                    updateVendorStatusMutation.isLoading ||
                    vendor?.status === "closed"
                  }
                  className={`w-full p-4 rounded-lg border-2 transition-all ${
                    vendor?.status === "closed"
                      ? "border-red-300 bg-red-50 text-red-700"
                      : "border-gray-200 hover:border-red-300 hover:bg-red-50"
                  } ${
                    updateVendorStatusMutation.isLoading
                      ? "opacity-50 cursor-not-allowed"
                      : "cursor-pointer"
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <div className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center">
                        <PowerOff className="h-4 w-4 text-red-600" />
                      </div>
                      <div className="text-left">
                        <p className="font-medium">Temporarily Closed</p>
                        <p className="text-sm opacity-75">
                          No new orders accepted
                        </p>
                      </div>
                    </div>
                    {vendor?.status === "closed" && (
                      <CheckCircle className="h-5 w-5 text-red-600" />
                    )}
                  </div>
                </button>
              </div>

              {updateVendorStatusMutation.isLoading && (
                <div className="text-center py-2">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-500 mx-auto"></div>
                  <p className="text-sm text-secondary-600 mt-2">
                    Updating status...
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;
